/**
 * What a URL means.
 *
 * The shell held its view in `useState` and the fleet grid held its open asset
 * in a second one, so the address bar said `/` no matter what was on screen.
 * Nothing could be linked to: not a view, not an asset, and — the reason this
 * exists — not from an alert email, which is the one place a link would be read
 * by someone who is not already looking at the console.
 *
 * A pure module rather than a hook, for the same reason `fleet-state.ts` is one:
 * the interesting decisions here are about strings, and strings can be tested
 * without a browser. The hook that reads `location.hash` is four lines in
 * `App.tsx` and has no decisions in it.
 *
 * ── Why the hash, and not a path ─────────────────────────────────────────
 *
 * `#/fleet/<id>` is served by whatever already serves `/`. A path-based route
 * needs the static server to rewrite every unknown path to `index.html`, which
 * means nginx config in `deploy/`, and getting it wrong turns a deep link into
 * a 404 on the day someone first clicks one from an email. The hash never
 * reaches the server, so there is nothing to configure and nothing to get
 * wrong.
 *
 * ── The information architecture, 2026-09-15 ─────────────────────────────
 *
 * Eight destinations, organised by what an operator DOES, not by which of the
 * predecessor's menus a screen came from. The first shell reproduced the
 * predecessor's nineteen items faithfully — "a recreation first" — and most of
 * them were reports over the same devices, each promoted to a top-level
 * destination. An operator scanned nineteen labels to find the five they use.
 *
 * A destination may carry sub-views (`#/settings/users`), and the old
 * nineteen keys are kept as ALIASES so that every link ever put in an email or
 * a bookmark still lands where it meant to.
 */

export const VIEW_KEYS = [
  'fleet',
  'alerts',
  'sites',
  'uptime',
  'footage',
  'sim',
  'activity',
  'power',
  'settings',
] as const;

export type ViewKey = (typeof VIEW_KEYS)[number];

export const DEFAULT_VIEW: ViewKey = 'fleet';

/**
 * The sub-views each destination has, first one the default.
 *
 * A runtime table rather than a type, because a URL carries a string somebody
 * may have typed, truncated or wrapped in an email client — and the only way
 * to answer "is this one of ours" is to have the list at runtime.
 */
export const SUB_VIEWS: Record<ViewKey, readonly string[]> = {
  /*
   * Decision 2 (2026-09-15): the fleet opens as a faults-first device table,
   * with the site cards one tab away. The grouping is in the address so a link
   * to "the fleet by site" means that, and closing an asset returns to the
   * grouping it was opened from.
   */
  fleet: ['devices', 'sites'],
  alerts: [],
  sites: ['list', 'map'],
  uptime: ['assets', 'report', 'matrix'],
  footage: ['recordings', 'pulls', 'vision'],
  sim: [],
  activity: [],
  power: ['solar', 'triage'],
  settings: ['users', 'channels', 'routing', 'mail', 'portal', 'credentials', 'security', 'hosts', 'remote', 'coverage', 'platform'],
};

/**
 * The predecessor-shaped keys the first shell used, and where each one lives
 * now. Kept so that `#/users` in a bookmark still opens Users.
 */
const ALIASES: Record<string, { view: ViewKey; sub: string | null }> = {
  map: { view: 'sites', sub: 'map' },
  recordings: { view: 'footage', sub: 'recordings' },
  pulls: { view: 'footage', sub: 'pulls' },
  vision: { view: 'footage', sub: 'vision' },
  solar: { view: 'power', sub: 'solar' },
  triage: { view: 'power', sub: 'triage' },
  status: { view: 'settings', sub: 'coverage' },
  users: { view: 'settings', sub: 'users' },
  email: { view: 'settings', sub: 'channels' },
  routing: { view: 'settings', sub: 'routing' },
  credentials: { view: 'settings', sub: 'credentials' },
  security: { view: 'settings', sub: 'security' },
};

/**
 * The asset drawer's sections, as they appear in a URL. The first is the
 * default and is never written, so `#/fleet/<id>` keeps meaning what every
 * alert email already sent means by it.
 */
export const ASSET_TABS = ['status', 'history', 'alerts', 'capabilities', 'edit'] as const;
export type AssetTab = (typeof ASSET_TABS)[number];

function isAssetTab(value: string | undefined): value is AssetTab {
  return value !== undefined && (ASSET_TABS as readonly string[]).includes(value);
}

/** Where the shell is. `assetId` is meaningful only on the fleet view, where `sub` is its grouping. */
export interface Route {
  view: ViewKey;
  /** The sub-view, or the destination's default when it has any. */
  sub: string | null;
  assetId: string | null;
  /**
   * The open asset's drawer section, when it is not the first. Absent rather
   * than `'status'` so a route built without thinking about tabs means the
   * default, and so every existing `{ view, sub, assetId }` still compares equal.
   */
  assetTab?: AssetTab;
  /**
   * One site's overview (`#/fleet/site/<id>`): its counters, its card and its
   * devices, and nothing else. Absent everywhere else, for the same reason
   * `assetTab` is: every existing route still compares equal.
   */
  siteId?: string;
}

export interface ParsedRoute {
  route: Route;
  /**
   * The view segment as it appeared, when it is not a view this build has.
   *
   * Kept rather than discarded so the shell can SAY it fell back. A URL naming
   * a view that was renamed — or a view a future build adds — should not land
   * silently on the fleet grid; that is the same failure as the asset case
   * below, one level up.
   */
  unknownView: string | null;
  /**
   * The asset segment as it appeared, when it is not a well-formed id.
   *
   * A hash is hand-editable and mail clients break long lines, so a truncated
   * uuid is a realistic arrival. Refusing it here keeps a malformed id out of
   * `/api/assets/<junk>/detail`, where Postgres rejects the uuid cast and the
   * screen gets a 500 that reads like the server is broken rather than like the
   * link is.
   */
  malformedAssetId: string | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isViewKey(value: string): value is ViewKey {
  return (VIEW_KEYS as readonly string[]).includes(value);
}

export function defaultSub(view: ViewKey): string | null {
  return SUB_VIEWS[view][0] ?? null;
}

/** A route on a destination, at its default sub-view. */
export function routeTo(view: ViewKey, sub: string | null = null): Route {
  const subs = SUB_VIEWS[view];
  const chosen = sub !== null && subs.includes(sub) ? sub : defaultSub(view);
  return { view, sub: chosen, assetId: null };
}

/**
 * Read a `location.hash` into a route.
 *
 * Total: every string produces a route, because the input is an address bar.
 * What it cannot make sense of it reports alongside the fallback rather than
 * dropping.
 */
export function parseHash(hash: string): ParsedRoute {
  /*
   * Everything after `?` is a screen's own state (Settings › Hosts keeps its
   * filter there, `#/settings/hosts?site=…`), read by that screen and not part
   * of where the shell is.
   */
  const trimmed = hash.replace(/^#/, '').replace(/^\//, '').replace(/\?.*$/, '');
  const segments = trimmed.split('/').filter((s) => s !== '');

  const empty: ParsedRoute = {
    route: routeTo(DEFAULT_VIEW),
    unknownView: null,
    malformedAssetId: null,
  };
  if (segments.length === 0) return empty;

  /*
   * `decodeURIComponent` can throw on a lone `%`, which a mail client that
   * mangled the link will happily produce. A segment that cannot be decoded is
   * not a view name, so treating the throw as "unknown" is the same answer the
   * next branch gives anyway.
   */
  let head: string;
  try {
    head = decodeURIComponent(segments[0] ?? '');
  } catch {
    head = segments[0] ?? '';
  }

  const alias = ALIASES[head];
  if (alias !== undefined) return { ...empty, route: routeTo(alias.view, alias.sub) };

  if (!isViewKey(head)) {
    return { ...empty, unknownView: head };
  }

  const tail = segments[1];
  if (tail === undefined) return { ...empty, route: routeTo(head) };

  /*
   * An asset only means something under `fleet`. Elsewhere the second segment
   * is a sub-view; one this build does not have is not an error worth
   * reporting — it is a URL from a build that had a sub-route this one does
   * not, and the destination it names is still the right place to land.
   */
  if (head !== 'fleet') return { ...empty, route: routeTo(head, tail) };

  /*
   * Under `fleet` the second segment is a grouping (`#/fleet/sites`, optionally
   * followed by an asset) or an asset on the default grouping (`#/fleet/<id>`,
   * the form every alert email carries). A grouping name can never be a uuid,
   * so the two cannot be confused.
   */
  let sub = defaultSub('fleet');
  let at = 1;
  /*
   * `#/fleet/site/<site id>[/<asset id>[/<tab>]]` — a site's overview, with an
   * asset optionally open over it so closing the drawer returns to the site.
   * "site" is not a grouping name, so it cannot be mistaken for one.
   */
  let siteId: string | undefined;
  if (tail === 'site') {
    const siteSegment = segments[2] ?? '';
    if (!UUID.test(siteSegment)) return { ...empty, route: routeTo('fleet', 'sites') };
    siteId = siteSegment.toLowerCase();
    sub = 'sites';
    at = 3;
    if (segments[at] === undefined) return { ...empty, route: { view: 'fleet', sub, assetId: null, siteId } };
  } else if (SUB_VIEWS.fleet.includes(tail)) {
    sub = tail;
    at = 2;
    if (segments[at] === undefined) return { ...empty, route: routeTo('fleet', sub) };
  }
  const assetSegment = segments[at] ?? '';

  if (!UUID.test(assetSegment)) {
    const fallback = siteId !== undefined ? { view: 'fleet' as const, sub, assetId: null, siteId } : routeTo('fleet', sub);
    return { ...empty, route: fallback, malformedAssetId: assetSegment };
  }

  // A section this build does not have opens the default one: the asset is still the right place.
  const tabSegment = segments[at + 1];
  const route: Route = { view: 'fleet', sub, assetId: assetSegment.toLowerCase() };
  if (siteId !== undefined) route.siteId = siteId;
  if (isAssetTab(tabSegment) && tabSegment !== ASSET_TABS[0]) route.assetTab = tabSegment;
  return { ...empty, route };
}

/** The hash a route should be written as. The inverse of `parseHash`. */
export function formatHash(route: Route): string {
  if (route.view === 'fleet' && route.siteId !== undefined) {
    const site = `#/fleet/site/${route.siteId}`;
    if (route.assetId === null) return site;
    const withAsset = `${site}/${route.assetId}`;
    return route.assetTab !== undefined && route.assetTab !== ASSET_TABS[0] ? `${withAsset}/${route.assetTab}` : withAsset;
  }
  if (route.view === 'fleet' && route.assetId !== null) {
    // The default grouping and section keep the short form alert emails already carry.
    const base =
      route.sub !== null && route.sub !== defaultSub('fleet')
        ? `#/fleet/${route.sub}/${route.assetId}`
        : `#/fleet/${route.assetId}`;
    return route.assetTab !== undefined && route.assetTab !== ASSET_TABS[0] ? `${base}/${route.assetTab}` : base;
  }
  if (route.sub !== null && route.sub !== defaultSub(route.view)) {
    return `#/${route.view}/${route.sub}`;
  }
  return `#/${route.view}`;
}

/**
 * The absolute URL for one asset, for anything outside the browser.
 *
 * Lives here so the email and the address bar cannot drift: a link built by
 * hand in the mail body is a link nobody re-checks when this format changes.
 * `base` is the console's origin — trailing slash or not, both are accepted,
 * because whoever sets the environment variable should not have to know.
 */
export function assetUrl(base: string, assetId: string): string {
  return `${base.replace(/\/+$/, '')}/${formatHash({ view: 'fleet', sub: null, assetId })}`;
}

/**
 * What the screen says when a deep link names an asset it cannot show.
 *
 * 🔴 THE FALLBACK IS NOT ALLOWED TO BE SILENT, and the reason is specific to
 * where these links come from. Someone clicking one is reading an alert that
 * names a camera; landing them on the fleet grid with no word said means they
 * start looking for that camera by eye, and the most likely reason the link did
 * not resolve is that they are in the WRONG TENANT — where the search will
 * succeed against a different site with a similar code. A silent fallback turns
 * a permission boundary into a misidentified camera.
 *
 * ⚠️ "Cannot see" is the strongest true statement available, and deliberately
 * weaker than "does not exist". `/api/assets/:id/detail` answers 404 for an
 * asset in another tenant and 404 for an id that was never issued, because
 * distinguishing them would turn the endpoint into an oracle for what exists
 * elsewhere on the platform. So this text must not claim the asset is gone —
 * it says what this session can establish, which is that this tenant cannot
 * open it, and points at the one thing the reader can act on.
 */
export interface DeepLinkFailure {
  title: string;
  detail: string;
}

export function unresolvableAssetLink(assetId: string): DeepLinkFailure {
  return {
    title: 'That link points to an asset this tenant cannot open',
    detail:
      `Nothing here matches ${assetId}. The usual cause is that the link is for a ` +
      `different tenant than the one you are signed in to — sign out and re-enter ` +
      `under that tenant, then open the link again. The address bar still holds it. ` +
      `If you are in the right tenant, the asset was never issued to it.`,
  };
}

export function malformedAssetLink(segment: string): DeepLinkFailure {
  return {
    title: 'That link is damaged',
    detail:
      `"${segment}" is not an asset id. Mail clients wrap long lines, so the most ` +
      `likely cause is a link that was broken in half on its way here — try ` +
      `copying the whole address out of the original message.`,
  };
}

export function unknownViewLink(segment: string): DeepLinkFailure {
  return {
    title: `There is no "${segment}" screen in this build`,
    detail: `Showing the fleet instead. The link may be from a newer build, or the screen may have been renamed.`,
  };
}

/**
 * The invitation token in `#/invite/<token>`, or null.
 *
 * Outside the view table on purpose: an invitation is opened by somebody with
 * no session, before the shell and its routes exist for them. The token is
 * passed through untouched; the API is what decides whether it is one of ours.
 */
export function inviteTokenFromHash(hash: string): string | null {
  const match = /^#\/?invite\/([^/?#]+)\/?$/.exec(hash);
  if (match === null) return null;
  try {
    return decodeURIComponent(match[1] ?? '');
  } catch {
    return null;
  }
}
